// Client-side display helpers for the call-out SLA admin surfaces (Session ADQ, UI half). Pure, so
// they unit test directly, and DOM-free so the office page and the customer section share one source.
//
// WHY formatSlaMinutes IS MIRRORED HERE
// The canonical helper lives in server/src/modules/sla/slaClock.ts, which the SPA cannot import: it
// is a server module, and a runtime import would pull server code into the browser bundle. So this
// is a deliberate READ-ONLY MIRROR, pinned to the server's behaviour by slaFormat.parity.test.ts
// (the same client/server mirror pattern as the surcharge outward-code lists in quotePricing.ts and
// the funnel classification in quoteFunnel.ts). Change one, change both: the parity test fails
// otherwise. The existing src/lib/timeOnSite.ts formatDurationHm is NOT reused: it renders 45 as
// "45m" where the SLA helper renders "45 min", and it describes engineer time on site, not a
// response-time commitment.
import type { CustomerSlaZoneFallback, PlatformSlaTargets } from "./api";

// A duration for the office, in plain English. 45 -> "45 min"; 90 -> "1h 30m"; 240 -> "4h".
// MIRROR of formatSlaMinutes in server/src/modules/sla/slaClock.ts. Pure.
export function formatSlaMinutes(mins: number | null | undefined): string {
  if (mins == null || !Number.isFinite(mins)) return "Not recorded";
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

// A TARGET for display. A null target is "No target set", never "Not recorded": per the locked
// honesty rule a leg nobody set a target for is `not_targeted`, which is neither a pass, a failure,
// nor missing data. ELE publishes no fix-time promise, so a blank fix target is the normal state and
// must not read like an error. Pure.
export function formatSlaTarget(mins: number | null | undefined): string {
  return mins == null ? "No target set" : formatSlaMinutes(mins);
}

// The same phrase mid-sentence (see describeSlaFallback).
function targetPhrase(mins: number | null): string {
  return mins == null ? "no target set" : formatSlaMinutes(mins);
}

// Which of the three targets is being described.
export type SlaTargetField = "attendance" | "fix" | "entrapment";

// A non-positive stored value is treated as unset, mirroring slaPolicy.ts isUsable: a 0-minute
// target is a data slip, not a promise to arrive instantly.
function usable(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 0;
}

function zoneValue(field: SlaTargetField, zone: CustomerSlaZoneFallback): number | null {
  if (field === "attendance") return zone.attendanceTargetMins;
  if (field === "fix") return zone.fixTargetMins;
  return zone.entrapmentAttendanceTargetMins;
}

function platformValue(field: SlaTargetField, platform: PlatformSlaTargets): number | null {
  if (field === "attendance") return platform.attendanceTargetMins;
  if (field === "fix") return platform.fixTargetMins;
  return platform.entrapmentAttendanceTargetMins;
}

// The target that actually applies in one zone for one field: the zone's own value when it has a
// usable one, else the platform default. Mirrors the resolveSlaPolicy fallback chain's lower two
// rungs (zone -> platform), so what the office is shown is what the clock will do. Pure.
export function effectiveFallbackMins(
  field: SlaTargetField,
  zone: CustomerSlaZoneFallback,
  platform: PlatformSlaTargets,
): number | null {
  const own = zoneValue(field, zone);
  return usable(own) ? own : platformValue(field, platform);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// The customer's REAL fallback for one field, in plain English: what a cleared field actually drops
// to. This is deliberately built from zoneFallbacks (the zones this customer's sites sit in), NOT
// from `platform`. The chain is customer -> zone -> platform, so telling an admin that clearing the
// field reverts to the platform's 60 minutes would be a lie whenever their sites sit in a zone with
// a different target.
//
// Only when the customer has no zone-placed site at all does the platform default genuinely apply,
// and only then is it named as such.
//
// NOTE on the unplaced wording: `unplacedSites` counts sites that could not be placed in a zone for
// EITHER reason - no readable postcode, or a postcode no zone claims when no fallback zone exists
// (which includes a tenant with no zones set up at all, where every site is unplaced). So this says
// "not in any zone" rather than asserting a missing postcode, which would be false in the second
// case. The payload cannot distinguish them, so it does not guess. Pure.
export function describeSlaFallback(
  field: SlaTargetField,
  fallbacks: { zones: CustomerSlaZoneFallback[]; unplacedSites: number },
  platform: PlatformSlaTargets,
): string {
  const { zones, unplacedSites } = fallbacks;
  const platformText = targetPhrase(platformValue(field, platform));

  if (zones.length === 0) {
    const base = `Without an override: ${platformText} (the platform default).`;
    return unplacedSites > 0 ? `${base} None of this customer's sites are in a coverage zone.` : base;
  }

  const entries = zones.map((z) => {
    const mins = effectiveFallbackMins(field, z, platform);
    return `${targetPhrase(mins)} (${z.name}, ${z.siteCount} ${plural(z.siteCount, "site", "sites")})`;
  });
  const base = `Without an override: ${entries.join(", ")}.`;
  if (unplacedSites === 0) return base;
  const verb = plural(unplacedSites, "is", "are");
  const uses = plural(unplacedSites, "it uses", "they use");
  return `${base} ${unplacedSites} ${plural(unplacedSites, "site", "sites")} ${verb} not in any zone, so ${uses} the platform default (${platformText}).`;
}

// ---- Outward codes ---------------------------------------------------------
// The office types a zone's postcode areas as a comma or space separated list. This only splits and
// tidies: it deliberately does NOT validate. The server's normaliseOutwardCodes is the authority and
// returns a plain-English message naming the offenders, which the UI shows verbatim. Re-implementing
// the rule here would risk the two drifting and rejecting a code the server would accept. Pure.
export function parseOutwardCodes(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const c = raw.trim().toUpperCase();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

// A zone's codes back into the text field. Pure.
export function formatOutwardCodes(codes: readonly string[]): string {
  return codes.join(", ");
}

// ---- Error text ------------------------------------------------------------
// The office message for a failed SLA write. The server is the source of the wording, so this never
// invents copy when the server supplied some:
//   - a 409 (zone_exists / fallback_exists / outward_code_claimed / override_exists) carries a
//     plain-English `message`, which apiRequest puts on ApiError.message. Show it verbatim.
//   - a 400 carries NO top-level message: the plain-English text is per field in `details`
//     (the zod messages). Join those rather than surfacing the raw "invalid_request" code.
//   - anything else falls back to the caller's own sentence.
// Structural (no ApiError import) so it stays pure and testable. Pure.
export function slaApiErrorText(err: unknown, fallback: string): string {
  const e = err as { details?: Record<string, string[]>; message?: unknown } | null;
  const details = e?.details;
  if (details && typeof details === "object") {
    const messages = Object.values(details)
      .flat()
      .filter((m): m is string => typeof m === "string" && m.trim() !== "");
    if (messages.length > 0) return messages.join(" ");
  }
  const message = typeof e?.message === "string" ? e.message.trim() : "";
  // A bare machine code ("invalid_request", "not_found") is never shown to the office.
  if (message && !/^[a-z][a-z0-9_]*$/.test(message)) return message;
  return fallback;
}
